import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchUser } from "../../store/common/authSlice";
import EmployeeAuthModal from "./employeeAuthModal";
import {
  canEmployeeLoginOnPortal,
  isAdminLikeEmployee,
  canAccessPhotoRecolour,
} from "../../utils/employeeRoles";
import { canAccessSurgeryBookingAdmin } from "../../utils/surgeryBookingAccess";

interface Props {
  allowedRoles: string[];
  allocationRoles?: string[];
}

export default function CheckAuth({
  allowedRoles,
}: Props) {
  const location = useLocation();
  const dispatch = useAppDispatch();

  const { isAuth, user, isLoading } = useAppSelector((state) => state.auth);
  const { employee } = useAppSelector((state) => state.allocationAuth);
  const [initialized, setInitialized] = useState(false);

  const isEmployeeDashboard = location.pathname.startsWith("/dashboard/employee");
  const isHrDashboard = location.pathname.startsWith("/dashboard/hr");
  const isPhotoRecolourPath =
    location.pathname.startsWith("/dashboard/photo-recolour") ||
    location.pathname.startsWith("/dashboard/admin/photo-recolour-packages");
  const isBookSurgeryPath = location.pathname.startsWith("/dashboard/book-surgery");
  const isSurgerySlotsAdminPath = location.pathname.startsWith(
    "/dashboard/admin/surgery-slots",
  );

  useEffect(() => {
    let isMounted = true;

    if (!isAuth) {
      dispatch(fetchUser()).finally(() => {
        if (isMounted) setInitialized(true);
      });
    } else {
      setInitialized(true);
    }

    return () => {
      isMounted = false;
    };
  }, [dispatch, isAuth]);

  if (!initialized || (isLoading && !user)) return null;

  if (!isAuth && !employee) {
    return <Navigate to="/dashboard" state={{ from: location }} replace />;
  }

  if (!user || !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  const canAccessPhotoRecolourPage = canAccessPhotoRecolour(
    user.role,
    employee?.role,
    user.verifyPhotoRecolour,
  );

  if (isPhotoRecolourPath && !canAccessPhotoRecolourPage) {
    return <Navigate to="/dashboard" replace />;
  }

  if (
    isBookSurgeryPath &&
    !(
      user.role === "User" &&
      !!user.verifySurgeryBooking &&
      String(user.region || "")
        .trim()
        .toUpperCase() === "UK"
    )
  ) {
    return <Navigate to="/dashboard" replace />;
  }

  if (
    isSurgerySlotsAdminPath &&
    !canAccessSurgeryBookingAdmin(user.role, employee?.role)
  ) {
    return <Navigate to="/dashboard" replace />;
  }

  // Cases Portal access is independent of Smile / Photo Recolour access.
  // A plain "User" needs `verified` to use Cases pages.
  if (user.role === "User" && !user.verified) {
    // Smile-only users have no Cases access at all -> send them to Smile.
    if (user.verifySmile && !isPhotoRecolourPath) {
      return <Navigate to="/dashboard/smiledesign/profile" replace />;
    }

    if (user.verifyPhotoRecolour) {
      if (!isPhotoRecolourPath && location.pathname !== "/dashboard/profile") {
        return <Navigate to="/dashboard/photo-recolour" replace />;
      }
    } else if (!user.verifySmile && location.pathname !== "/dashboard/profile") {
      // Brand-new users (no portal access) may only use the profile page.
      return <Navigate to="/dashboard/profile" replace />;
    }
  }

  // Employee dashboard: Production portal → Designer/Sr Designer/Captain/Assistant Captain employee
  // Operations portal → CRM/Admin employee; Super Admin portal → no employee required
  if (isEmployeeDashboard) {
    if (user.role === "Super Admin") {
      return <Outlet />;
    }

    if (user.role === "Production" || user.role === "Operations") {
      if (!employee) {
        return (
          <>
            <Outlet />
            <EmployeeAuthModal onClose={() => {}} />
          </>
        );
      }

      if (!canEmployeeLoginOnPortal(employee.role, user.role)) {
        return <Navigate to="/dashboard" replace />;
      }

      return <Outlet />;
    }

    return <Navigate to="/dashboard" replace />;
  }

  if (isHrDashboard) {
    const isCrmAdmin =
      user.role === "Operations" && isAdminLikeEmployee(employee?.role);
    const isProductionAdmin =
      user.role === "Production" && employee?.role === "Admin";

    if (user.role === "Super Admin" || isCrmAdmin || isProductionAdmin) {
      return <Outlet />;
    }

    return <Navigate to="/dashboard" replace />;
  }

  if ((user.role === "Operations" || user.role === "Admin") && !employee) {
    return (
      <>
        <Outlet />
        <EmployeeAuthModal onClose={() => {}} />
      </>
    );
  }

  if (
    (user?.role === "User" || user?.role === "Side Admin") &&
    location.pathname.startsWith("/dashboard/allocation")
  ) {
    return <Navigate to="/dashboard/profile" replace />;
  }

  return <Outlet />;
}
